import { buildCustomAttributeJsonPath } from '@provision/attribute-utils';
import { JsonFieldTypeCast } from '@provision/entity-types';
import { stableStringify } from '@provision/formatting-utils';
import {
  DocumentNode,
  useApolloClient,
  useFragment,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo, useRef } from 'react';
import {
  CustomAttributeType,
  CustomAttributeValue,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { getCustomAttributeConfig } from 'src/graphql/entities/custom-attributes/field-view-registry';
import {
  parseStoredAttributes,
  resolveStoredValue,
} from 'src/graphql/entities/custom-attributes/payload';
import { QueryVariables, Records } from 'src/graphql/types';

export interface StoredAttributesEntityDescriptor {
  typename: string;
  idAttribute: string;
  fragment: DocumentNode;
  fragmentName: string;
  query: DocumentNode;
  queryRootField: string;
  mutation: DocumentNode;
  mutationRootField: string;
}

interface StoredAttributesRecord {
  id: string;
  customAttributes: string | null;
  [idAttribute: string]: unknown;
}

interface StoredAttributesFragmentData {
  __typename: string;
  id: string;
  customAttributes: string | null;
  [idAttribute: string]: unknown;
}

interface SetFieldValueInput {
  entityId: string;
  key: string;
  type: CustomAttributeType;
  value: CustomAttributeValue;
  /** The definition's default, which is what a key the entity never stored renders as. */
  defaultValue?: unknown;
}

/**
 * What a cleared field leaves behind: the backend's per-key merge keeps the key
 * holding an empty value instead of removing it, and the cache has to record the
 * same thing — an absent key falls back to the definition's `defaultValue`, so
 * dropping it would put the old value back on screen. Mirrors the cleared
 * branches of `serializeForDynamicWrite`.
 */
function getClearedStoredValue(type: CustomAttributeType): unknown {
  if (type === 'multi_select') return [];
  if (type === 'checkbox') return false;

  return '';
}

// serializes one edited value into the cast-tagged string the backend's JSONB merge expects.
function serializeForDynamicWrite(
  type: CustomAttributeType,
  encoded: unknown,
): { cast: JsonFieldTypeCast; value: string } {
  if (type === 'multi_select') {
    return { cast: '::text[]', value: JSON.stringify(Array.isArray(encoded) ? encoded : []) };
  }

  if (type === 'checkbox') {
    return { cast: '::boolean', value: encoded === true ? 'true' : 'false' };
  }

  // eslint-disable-next-line
  return { cast: '::text', value: encoded == null ? '' : String(encoded) };
}

export function mergeEditedKeysOntoServer(
  serverJson: string | null | undefined,
  localJson: string | null | undefined,
  editedKeys: Iterable<string>,
): string {
  const merged = parseStoredAttributes(serverJson ?? null).values;
  const local = parseStoredAttributes(localJson ?? null).values;

  for (const editedKey of editedKeys) {
    if (editedKey in local) merged[editedKey] = local[editedKey];
    else delete merged[editedKey];
  }

  return JSON.stringify(merged);
}

/**
 * Builds the per-entity `useStoredAttributes` / `useUpdateStoredAttributes` hook
 * pair for `CUSTOM_ATTRIBUTES_CONFIG`. The read/write logic — optimistic per-key
 * cache merges, serialized network writes, and the response re-merge — is
 * entity-agnostic; only the documents and id attribute differ per entity.
 */
export function getStoredAttributeHooks(descriptor: StoredAttributesEntityDescriptor) {
  const {
    typename,
    idAttribute,
    fragment,
    fragmentName,
    query,
    queryRootField,
    mutation,
    mutationRootField,
  } = descriptor;

  // a deleted site, client or conversation still shows its attributes — the panel locks them
  const includeDeleted = true;

  function useStoredAttributes(entityId: string | undefined) {
    const { data, loading } = useQuery<
      Record<string, Records<StoredAttributesRecord>>,
      QueryVariables
    >(query, {
      variables: {
        params: {
          filters: [{ attribute: 'id', operator: '=', value: entityId! }],
          limit: 1,
          includeDeleted,
        },
      },
      skip: !entityId,
    });

    const customAttributes = useMemo(
      () => data?.[queryRootField]?.records?.[0]?.customAttributes ?? null,
      [data],
    );

    return { customAttributes, loading };
  }

  /** Requests only the entities the cache has not read yet, so a growing list fetches its new rows alone. */
  function useLoadStoredAttributes(entityIds: readonly string[]) {
    const { cache } = useApolloClient();
    const unreadIds = entityIds.filter(
      (id) =>
        !cache.readFragment({
          id: cache.identify({ __typename: typename, id }),
          fragment,
          fragmentName,
        }),
    );

    useStoredAttributesByIds(unreadIds);
  }

  /** One query for several entities of this type — the policies tab needs every site linked to a conversation. */
  function useStoredAttributesByIds(entityIds: readonly string[]) {
    const { data, loading } = useQuery<
      Record<string, Records<StoredAttributesRecord>>,
      QueryVariables
    >(query, {
      variables: {
        params: {
          filters: [{ attribute: 'id', operator: 'in', value: entityIds.join(',') }],
          limit: entityIds.length,
          includeDeleted,
        },
      },
      skip: entityIds.length === 0,
    });

    const customAttributesById = useMemo(
      () =>
        new Map<string, string | null>(
          (data?.[queryRootField]?.records ?? []).map((record) => [
            String(record[idAttribute]),
            record.customAttributes,
          ]),
        ),
      [data],
    );

    return { customAttributesById, loading };
  }

  /** Reads what a batch read (`useStoredAttributesByIds`) normalized into the cache — no request of its own. */
  function useCachedStoredAttributes(entityId: string) {
    const { data, complete } = useFragment<StoredAttributesFragmentData>({
      fragment,
      fragmentName,
      from: { __typename: typename, id: entityId },
    });

    return { customAttributes: complete ? data.customAttributes : null, loading: !complete };
  }

  function useUpdateStoredAttributes() {
    const { cache } = useApolloClient();

    const [updateEntity] =
      useMutation<Record<string, StoredAttributesFragmentData[]>>(mutation);

    // serialize the network writes so two quick edits to the same key persist in order.
    const lastPromiseRef = useRef<Promise<unknown>>(Promise.resolve());

    const latestJsonRef = useRef<Record<string, string>>({});
    const editedKeysRef = useRef<Record<string, Set<string>>>({});

    const setFieldValue = useCallback(
      (input: SetFieldValueInput) => {
        const { entityId, key, type, value, defaultValue } = input;

        const cacheId = cache.identify({ __typename: typename, id: entityId });

        // Only nullable in theory: every entity here is normalized by `id`.
        if (!cacheId) {
          return Promise.resolve();
        }

        const cached = cache.readFragment<StoredAttributesFragmentData>({
          id: cacheId,
          fragment,
          fragmentName,
        });
        const current = parseStoredAttributes(cached?.customAttributes ?? null).values;
        const field = getCustomAttributeConfig(type);
        const encoded = field.encode(value);
        const stored = encoded === null ? getClearedStoredValue(type) : encoded;

        // Nothing changed (e.g. a blur with no edit) — skip the network round-trip. Compare what
        // the row actually renders rather than the raw entries: a key the entity never stored
        // shows the definition's default, so clearing that one is a real edit, while the stored
        // and authored spellings of the same value (`5` vs `"5"`) are not.
        const currentValue = field.decode(resolveStoredValue(current, key, defaultValue));

        if (stableStringify(currentValue) === stableStringify(field.decode(stored))) {
          return Promise.resolve();
        }

        // optimistic local merge of the single edited key so other views (grid) reflect it at once.
        const next = { ...current, [key]: stored };
        const nextJson = JSON.stringify(next);
        latestJsonRef.current[entityId] = nextJson;
        (editedKeysRef.current[entityId] ??= new Set()).add(key);
        cache.writeFragment<StoredAttributesFragmentData>({
          id: cacheId,
          fragment,
          fragmentName,
          data: {
            __typename: typename,
            id: entityId,
            [idAttribute]: entityId,
            customAttributes: nextJson,
          },
        });

        // Persist ONLY the edited key as a JSONB merge (custom_attributes || jsonb_set(...)). A
        // whole-blob write would re-assert every sibling key from this snapshot, so a stale base
        // (the shared, persisted, multi-writer cache) silently reverted other attributes. The
        // mutation response returns the full merged blob, which Apollo normalizes back onto the entity.
        const { cast, value: wireValue } = serializeForDynamicWrite(type, encoded);

        const previousWrite = lastPromiseRef.current;
        const runWrite = async () => {
          // Queue behind the previous write regardless of how it ended; its failure
          // is already surfaced to whoever awaited it.
          try {
            await previousWrite;
          } catch {
            /* the previous write's caller owns that error */
          }

          await updateEntity({
            variables: {
              conditions: [{ attribute: idAttribute, operator: '=', value: entityId }],
              data: {
                dynamicAttributes: [
                  { attribute: buildCustomAttributeJsonPath(key, cast), value: wireValue },
                ],
              },
            },
            update: (proxy, result) => {
              const editedKeys = editedKeysRef.current[entityId];
              if (!editedKeys?.size) return;

              const serverJson =
                result.data?.[mutationRootField]?.[0]?.customAttributes ?? null;

              proxy.writeFragment<StoredAttributesFragmentData>({
                id: cacheId,
                fragment,
                fragmentName,
                data: {
                  __typename: typename,
                  id: entityId,
                  [idAttribute]: entityId,
                  customAttributes: mergeEditedKeysOntoServer(
                    serverJson,
                    latestJsonRef.current[entityId] ?? null,
                    editedKeys,
                  ),
                },
              });
            },
          });
        };

        // Assigned before the first await inside runWrite resolves, so the next
        // call in the same tick queues behind this one.
        const networkWrite = runWrite();
        lastPromiseRef.current = networkWrite;

        return networkWrite;
      },
      [cache, updateEntity],
    );

    return { setFieldValue };
  }

  return {
    useStoredAttributes,
    useLoadStoredAttributes,
    useStoredAttributesByIds,
    useCachedStoredAttributes,
    useUpdateStoredAttributes,
  };
}
